import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { CanvasState, CanvasSnapshot } from './canvas.types';
import type { PipelineNode } from '../../components/canvas/types/canvas.types';
import type { Edge } from '@xyflow/react';

const initialState: CanvasState = {
    present: { nodes: [], edges: [] },
    past: [],
    future: [],
};

const MAX_HISTORY = 50;

const clone = <T>(data: T): T => JSON.parse(JSON.stringify(data));
const isSameSnapshot = (a: CanvasSnapshot, b: CanvasSnapshot): boolean =>
    JSON.stringify(a) === JSON.stringify(b);

const pushToPast = (state: CanvasState) => {
    state.past.push(clone(state.present));
    if (state.past.length > MAX_HISTORY) {
        state.past.shift();
    }
};

const canvasSlice = createSlice({
    name: 'canvas',
    initialState,
    reducers: {
        /** Throttled full sync */
        syncCanvasState: (
            state,
            action: PayloadAction<CanvasSnapshot>
        ) => {
            if (isSameSnapshot(state.present, action.payload)) {
                return;
            }
            pushToPast(state);
            state.present = clone(action.payload);
            state.future = [];
        },

        /** Add node */
        addNode: (state, action: PayloadAction<PipelineNode>) => {
            pushToPast(state);
            state.present.nodes.push(action.payload);
            state.future = [];
        },

        /** Delete node */
        deleteNode: (state, action: PayloadAction<string>) => {
            pushToPast(state);

            state.present.nodes = state.present.nodes.filter(
                n => n.id !== action.payload
            );

            state.present.edges = state.present.edges.filter(
                e => e.source !== action.payload && e.target !== action.payload
            );

            state.future = [];
        },

        /** Add edge */
        addEdgeAction: (state, action: PayloadAction<Edge>) => {
            pushToPast(state);
            state.present.edges.push(action.payload);
            state.future = [];
        },

        /** Clear canvas */
        clearCanvas: (state) => {
            pushToPast(state);
            state.present = { nodes: [], edges: [] };
            state.future = [];
        },

        /** Undo */
        undo: (state) => {
            const prev = state.past.pop();
            if (!prev) return;

            state.future.push(clone(state.present));
            state.present = prev;
        },

        /** Redo */
        redo: (state) => {
            const next = state.future.pop();
            if (!next) return;

            state.past.push(clone(state.present));
            state.present = next;
        },
    },
});

export const {
    syncCanvasState,
    addNode,
    deleteNode,
    addEdgeAction,
    clearCanvas,
    undo,
    redo,
} = canvasSlice.actions;

export default canvasSlice.reducer;
